/* eslint-disable max-len */
/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Col,
  Row,
  Tooltip,
  Typography,
} from 'antd';
import {
  VirtualizedTable,
} from '@deltaohm/ant-components';
import { DownloadOutlined } from '@ant-design/icons';

import {
  getLocale,
  formatLocaleWithTimezone,
} from '../../../utils/date';

import { exportExcel, PERCENT_DECIMAL } from '../../charts/utils';

const TIMESTAMP_WIDTH = 250;
const VALUE_WIDTH = 125;

const StatisticsMeasureTableModal = (props) => {
  const {
    soundSession,
    measure,
    height,
    currentConfig,
  } = props;

  const { timezone } = soundSession;

  const { i18n, t } = useTranslation();
  const locale = getLocale(i18n.language);

  const thisConfig = React.useMemo(() => {
    const result = measure.config.find((c) => c.measureId === currentConfig.measureId);
    return result;
  }, [measure, currentConfig]);

  const columns = React.useMemo(() => {
    const result = [
      {
        title: thisConfig.label,
        dataIndex: 'c',
        render: (c) => (
          <div style={{ textAlign: 'right', width: '100%' }}>
            <Typography.Text style={{ fontFamily: 'monospace' }}>
              {`${c[0]}-${c[1]}`}
            </Typography.Text>
          </div>
        ),
        width: VALUE_WIDTH,
      },
      {
        title: t('common.value'),
        dataIndex: 'value',
        render: (value) => {
          let text;
          if (value != null) {
            text = value.toFixed(PERCENT_DECIMAL);
          }
          return (
            <div style={{ textAlign: 'right', width: '100%' }}>
              <Typography.Text style={{ fontFamily: 'monospace' }}>
                {(text * 100).toFixed(2)}
                %
              </Typography.Text>
            </div>
          );
        },
        width: VALUE_WIDTH,
      },
      {
        title: 'timestamp',
        dataIndex: 'timestamp',
        render: (timestamp) => {
          const text = formatLocaleWithTimezone(timestamp, 'yyyy/MM/dd HH:mm:ss:SSS', locale, timezone, false);
          return (
            <div style={{ textAlign: 'right', width: '100%' }}>
              <Typography.Text style={{ fontFamily: 'monospace' }}>
                {text}
              </Typography.Text>
            </div>
          );
        },
        width: TIMESTAMP_WIDTH,
      },
    ];
    return result;
  }, [t, locale, timezone, thisConfig]);

  const dataSource = React.useMemo(() => {
    const { values } = measure;
    const sum = values[0].data[thisConfig.measureId].reduce((acc, v) => acc + v, 0);
    const result = thisConfig.classes.map((c, index) => {
      const data = {
        c,
        value: (values[0].data[thisConfig.measureId][index] / sum),
        timestamp: values[0].timestamp,
      };
      return data;
    });
    return result;
  }, [measure, thisConfig]);

  const handleDownloadExcel = () => {
    const header = ['timestamp', 'value', thisConfig.label];

    const data = dataSource.map((ds) => ([
      formatLocaleWithTimezone(ds.timestamp, 'yyyy-MM-dd HH:mm:ss.SSS', locale, timezone),
      ds.value,
      `${ds.c[0]}-${ds.c[1]}`,
    ]));
    exportExcel(
      `${soundSession.name}_${thisConfig.label}_${soundSession.timezone}.xlsx`,
      'Statistics',
      [
        header,
        ...data,
      ],
    );
  };

  return (
    <>
      <Row justify="end">
        <Col>
          <Tooltip title={t('common.downloadExcel')}>
            <Button shape="circle" size="small" type="primary" onClick={handleDownloadExcel}>
              <DownloadOutlined />
            </Button>
          </Tooltip>
        </Col>
      </Row>
      <div style={{ height: 10 }} />
      <VirtualizedTable
        height={height}
        bordered
        size="small"
        columns={columns}
        rowKey={(record) => record.parameter}
        dataSource={dataSource}
        pagination={false}
      />
    </>
  );
};

const propTypes = {
  soundSession: PropTypes.shape({
    timezone: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  measure: PropTypes.shape({
    kind: PropTypes.string,
    config: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
    values: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  currentConfig: PropTypes.shape({
    measureId: PropTypes.string.isRequired,
  }).isRequired,
  height: PropTypes.number,
};

const defaultProps = {
  height: 1500,
};

StatisticsMeasureTableModal.propTypes = propTypes;
StatisticsMeasureTableModal.defaultProps = defaultProps;

export default StatisticsMeasureTableModal;
